import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { departmentLabel, type HospitalRole } from '@refdash/api-client';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

const selectClass = 'h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary';

const ROLE_LABELS: Record<string, string> = {
  hospital_rep: 'Hospital rep',
  coordinator: 'Coordinator',
  intake_coordinator: 'Intake coordinator',
  department_head: 'Department head',
  doctor: 'Doctor',
};

export function TeamSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hospitalId = user?.hospital_id ?? '';
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<HospitalRole>('coordinator');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [doctorId, setDoctorId] = useState<number | ''>('');

  const { data: users = [] } = useQuery({
    queryKey: ['hospital-users', hospitalId],
    queryFn: () => api.hospitals.listUsers(hospitalId),
    enabled: !!hospitalId,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });
  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', hospitalId],
    queryFn: () => api.hospitals.listDoctors(hospitalId),
    enabled: !!hospitalId,
  });

  function reset() {
    setIsAdding(false); setUsername(''); setDisplayName(''); setPassword('');
    setRole('coordinator'); setDepartmentId(''); setDoctorId('');
  }

  const doctorById = new Map(doctors.map((d) => [d.id, d]));
  const selectedDoctor = doctorId ? doctorById.get(doctorId) : undefined;
  // A doctor account is queue-scoped by department. Normally it derives from
  // the doctor record; an unassigned ("any department") doctor needs an
  // explicit choice.
  const doctorNeedsDeptChoice = role === 'doctor' && !!selectedDoctor && selectedDoctor.department_id == null;

  const createMutation = useMutation({
    mutationFn: () => api.hospitals.createUser(hospitalId, {
      username: username.trim(),
      password,
      display_name: displayName.trim(),
      role,
      department_id:
        role === 'department_head' || doctorNeedsDeptChoice ? (departmentId || null) : null,
      doctor_id: role === 'doctor' ? (doctorId || null) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-users'] });
      reset();
      toast.success('Team member added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.hospitals.deleteUser(hospitalId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-users'] });
      toast.success('Team member removed');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const needsDept = role === 'department_head';
  const needsDoctor = role === 'doctor';
  const ready = username.trim().length >= 3 && displayName.trim() && password.length >= 6
    && (!needsDept || departmentId) && (!needsDoctor || doctorId)
    && (!doctorNeedsDeptChoice || departmentId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (ready) createMutation.mutate();
  }

  const departmentById = new Map(departments.map((d) => [d.id, d]));

  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">Team</h2>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <CardTitle>Team members</CardTitle>
            </div>
            {!isAdding && (
              <Button variant="ghost" size="icon-sm" onClick={() => setIsAdding(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 px-1 group">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">{u.display_name}</p>
                <p className="text-xs text-muted-foreground">
                  {u.username} · {ROLE_LABELS[u.role] ?? u.role}
                  {u.department_id != null && departmentById.get(u.department_id) &&
                    ` · ${departmentLabel(departmentById.get(u.department_id)!)}`}
                </p>
              </div>
              {u.username !== user?.username && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => deleteMutation.mutate(u.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}

          {isAdding && (
            <form onSubmit={handleSubmit} className="space-y-2 pt-2 border-t border-border">
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Full name" required autoFocus />
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username (for sign-in)" required />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)" required />
              <select className={selectClass} value={role} onChange={(e) => setRole(e.target.value as HospitalRole)} aria-label="Role">
                <option value="hospital_rep">Hospital rep</option>
                <option value="coordinator">Coordinator</option>
                <option value="intake_coordinator">Intake coordinator</option>
                <option value="department_head">Department head</option>
                <option value="doctor">Doctor</option>
              </select>
              {needsDept && (
                <select className={selectClass} value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')} aria-label="Department">
                  <option value="">Select department…</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{departmentLabel(d)}</option>)}
                </select>
              )}
              {needsDoctor && (
                <>
                  <select className={selectClass} value={doctorId} onChange={(e) => setDoctorId(e.target.value ? Number(e.target.value) : '')} aria-label="Doctor record">
                    <option value="">Select doctor record…</option>
                    {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  {selectedDoctor && selectedDoctor.department_id != null && (
                    <p className="text-xs text-muted-foreground">
                      Queue scope: {departmentById.get(selectedDoctor.department_id)
                        ? departmentLabel(departmentById.get(selectedDoctor.department_id)!)
                        : 'department'} (from the doctor's specialty)
                    </p>
                  )}
                  {doctorNeedsDeptChoice && (
                    <select className={selectClass} value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')} aria-label="Department">
                      <option value="">This doctor has no specialty — choose their queue department…</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{departmentLabel(d)}</option>)}
                    </select>
                  )}
                </>
              )}
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={!ready || createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add member'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={reset}>Cancel</Button>
              </div>
            </form>
          )}

          {users.length === 0 && !isAdding && (
            <p className="text-sm text-muted-foreground py-3 text-center">No team members yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
